import React from 'react';
import { Order } from '../../pages/Orders';
import { CheckCircleIcon, XCircleIcon, ClockIcon, CalendarIcon, UserIcon, MailIcon, PhoneIcon, ShoppingCartIcon, AlertTriangleIcon, ChevronRightIcon } from 'lucide-react';
interface OrderListProps {
  orders: Order[];
  onOrderClick: (order: Order) => void;
}
export function OrderList({
  orders,
  onOrderClick
}: OrderListProps) {
  if (orders.length === 0) {
    return <div className="p-8 text-center" data-id="element-2445">
        <p className="text-gray-500" data-id="element-2446">No orders found</p>
      </div>;
  }
  // Get status badge with appropriate colors
  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'new':
        return <span className="px-2 py-1 text-xs rounded-full bg-blue-100 text-blue-800" data-id="element-2447">
            New
          </span>;
      case 'in-progress':
        return <span className="px-2 py-1 text-xs rounded-full bg-amber-100 text-amber-800" data-id="element-2448">
            In Progress
          </span>;
      case 'completed':
        return <span className="px-2 py-1 text-xs rounded-full bg-green-100 text-green-800" data-id="element-2449">
            Completed
          </span>;
      case 'cancelled':
        return <span className="px-2 py-1 text-xs rounded-full bg-red-100 text-red-800" data-id="element-2450">
            Cancelled
          </span>;
      case 'open':
        return <span className="px-2 py-1 text-xs rounded-full bg-purple-100 text-purple-800" data-id="element-2451">
            Open
          </span>;
      default:
        return <span className="px-2 py-1 text-xs rounded-full bg-gray-100 text-gray-800" data-id="element-2452">
            {status}
          </span>;
    }
  };
  // Get payment status icon
  const getPaymentStatusIcon = (status: string) => {
    switch (status) {
      case 'paid':
        return <CheckCircleIcon size={16} className="text-green-500" data-id="element-2453" />;
      case 'unpaid':
        return <ClockIcon size={16} className="text-amber-500" data-id="element-2454" />;
      case 'cancelled':
        return <XCircleIcon size={16} className="text-red-500" data-id="element-2455" />;
      default:
        return null;
    }
  };
  return <div className="overflow-x-auto" data-id="element-2456">
      <table className="min-w-full divide-y divide-gray-200" data-id="element-2457">
        <thead className="bg-gray-50" data-id="element-2458">
          <tr data-id="element-2459">
            <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2460">
              Order
            </th>
            <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2461">
              Date
            </th>
            <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2462">
              Customer
            </th>
            <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2463">
              Status
            </th>
            <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2464">
              Payment
            </th>
            <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2465">
              Total
            </th>
            <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2466">
              Items
            </th>
            <th scope="col" className="relative px-4 py-3" data-id="element-2467">
              <span className="sr-only" data-id="element-2468">Actions</span>
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200" data-id="element-2469">
          {orders.map(order => <tr key={order.id} onClick={() => onOrderClick(order)} className="hover:bg-gray-50 cursor-pointer" data-id="element-2470">
              <td className="px-4 py-4 whitespace-nowrap" data-id="element-2471">
                <div className="text-sm font-medium text-gray-900" data-id="element-2472">
                  #{order.id}
                </div>
                {order.transaction && <div className="text-xs text-gray-500 truncate max-w-[120px]" data-id="element-2473">
                    {order.transaction}
                  </div>}
              </td>
              <td className="px-4 py-4 whitespace-nowrap" data-id="element-2474">
                <div className="flex items-center text-sm text-gray-500" data-id="element-2475">
                  <CalendarIcon size={14} className="mr-1" data-id="element-2476" />
                  {order.date}
                </div>
                <div className="flex items-center text-sm text-gray-500" data-id="element-2477">
                  <ClockIcon size={14} className="mr-1" data-id="element-2478" />
                  {order.time}
                </div>
              </td>
              <td className="px-4 py-4" data-id="element-2479">
                <div className="flex items-center" data-id="element-2480">
                  <div className="flex-shrink-0 h-8 w-8 bg-gray-100 rounded-full flex items-center justify-center" data-id="element-2481">
                    <UserIcon size={14} className="text-gray-500" data-id="element-2482" />
                  </div>
                  <div className="ml-3" data-id="element-2483">
                    <div className="text-sm font-medium text-gray-900" data-id="element-2484">
                      {order.customer.name}
                    </div>
                    <div className="flex items-center text-xs text-gray-500" data-id="element-2485">
                      <MailIcon size={12} className="mr-1" data-id="element-2486" />
                      <span className="truncate max-w-[120px]" data-id="element-2487">
                        {order.customer.email}
                      </span>
                    </div>
                    <div className="flex items-center text-xs text-gray-500" data-id="element-2488">
                      <PhoneIcon size={12} className="mr-1" data-id="element-2489" />
                      {order.customer.mobileNumber}
                    </div>
                  </div>
                </div>
              </td>
              <td className="px-4 py-4 whitespace-nowrap" data-id="element-2490">
                {getStatusBadge(order.status)}
              </td>
              <td className="px-4 py-4 whitespace-nowrap" data-id="element-2491">
                <div className="flex items-center" data-id="element-2492">
                  {getPaymentStatusIcon(order.paymentStatus)}
                  <span className="ml-1.5 text-sm text-gray-700 capitalize" data-id="element-2493">
                    {order.paymentStatus}
                  </span>
                </div>
                {order.transactionStatus && <div className="text-xs text-gray-500 capitalize mt-1" data-id="element-2494">
                    {order.transactionStatus}
                  </div>}
              </td>
              <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-gray-900" data-id="element-2495">
                ${order.total.toFixed(2)}
              </td>
              <td className="px-4 py-4 whitespace-nowrap" data-id="element-2496">
                <div className="flex items-center text-sm text-gray-500" data-id="element-2497">
                  <ShoppingCartIcon size={14} className="mr-1" data-id="element-2498" />
                  <span data-id="element-2499">
                    {order.items.reduce((sum, item) => sum + item.quantity, 0)}{' '}
                    items
                  </span>
                </div>
                <div className="text-xs text-gray-500 truncate max-w-[120px] mt-1" data-id="element-2500">
                  {order.items.map(item => `${item.quantity}x ${item.name}`).join(', ')}
                </div>
              </td>
              <td className="px-4 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-2501">
                <ChevronRightIcon size={16} className="text-gray-400" data-id="element-2502" />
              </td>
            </tr>)}
        </tbody>
      </table>
    </div>;
}